// Compact Font Format programs (Adobe TN #5176) with Type 2 charstrings (TN #5177): glyph outlines by glyph id, name or CID.

import type { FontProgram, GlyphPath } from "./truetype";

const STD = ".notdef space exclam quotedbl numbersign dollar percent ampersand quoteright parenleft parenright asterisk plus comma hyphen period slash zero one two three four five six seven eight nine colon semicolon less equal greater question at A B C D E F G H I J K L M N O P Q R S T U V W X Y Z bracketleft backslash bracketright asciicircum underscore quoteleft a b c d e f g h i j k l m n o p q r s t u v w x y z braceleft bar braceright asciitilde exclamdown cent sterling fraction yen florin section currency quotesingle quotedblleft guillemotleft guilsinglleft guilsinglright fi fl endash dagger daggerdbl periodcentered paragraph bullet quotesinglbase quotedblbase quotedblright guillemotright ellipsis perthousand questiondown grave acute circumflex tilde macron breve dotaccent dieresis ring cedilla hungarumlaut ogonek caron emdash AE ordfeminine Lslash Oslash OE ordmasculine ae dotlessi lslash oslash oe germandbls onesuperior logicalnot mu trademark Eth onehalf plusminus Thorn onequarter divide brokenbar degree thorn threequarters twosuperior registered minus eth multiply threesuperior copyright Aacute Acircumflex Adieresis Agrave Aring Atilde Ccedilla Eacute Ecircumflex Edieresis Egrave Iacute Icircumflex Idieresis Igrave Ntilde Oacute Ocircumflex Odieresis Ograve Otilde Scaron Uacute Ucircumflex Udieresis Ugrave Yacute Ydieresis Zcaron aacute acircumflex adieresis agrave aring atilde ccedilla eacute ecircumflex edieresis egrave iacute icircumflex idieresis igrave ntilde oacute ocircumflex odieresis ograve otilde scaron uacute ucircumflex udieresis ugrave yacute ydieresis zcaron".split(" ");

interface Index { count: number; off: number[]; data: Uint8Array }

function readIndex(d: Uint8Array, pos: number): { idx: Index; end: number } {
    const count = (d[pos]! << 8) | d[pos + 1]!;
    if (count === 0) return { idx: { count: 0, off: [], data: d }, end: pos + 2 };
    const osz = d[pos + 2]!;
    const offs: number[] = [];
    let p = pos + 3;
    for (let i = 0; i <= count; i++) { let v = 0; for (let k = 0; k < osz; k++) v = v * 256 + d[p++]!; offs.push(v); }
    const base = p - 1;
    return { idx: { count, off: offs.map((o) => base + o), data: d }, end: base + offs[count]! };
}
const item = (ix: Index, i: number): Uint8Array => ix.data.subarray(ix.off[i]!, ix.off[i + 1]!);

function readDict(d: Uint8Array): Map<number, number[]> {
    const out = new Map<number, number[]>();
    let st: number[] = [];
    let i = 0;
    while (i < d.length) {
        const b = d[i++]!;
        if (b <= 21) {
            let op = b;
            if (b === 12) op = 1200 + d[i++]!;
            out.set(op, st);
            st = [];
        } else if (b === 28) { st.push(((d[i]! << 8) | d[i + 1]!) << 16 >> 16); i += 2; }
        else if (b === 29) { st.push((d[i]! << 24) | (d[i + 1]! << 16) | (d[i + 2]! << 8) | d[i + 3]!); i += 4; }
        else if (b === 30) {
            let s = "";
            for (let done = false; !done && i < d.length;) {
                const x = d[i++]!;
                for (const nb of [x >> 4, x & 15]) {
                    if (nb === 15) { done = true; break; }
                    s += nb < 10 ? String(nb) : nb === 10 ? "." : nb === 11 ? "E" : nb === 12 ? "E-" : nb === 14 ? "-" : "";
                }
            }
            st.push(parseFloat(s) || 0);
        }
        else if (b >= 32 && b <= 246) st.push(b - 139);
        else if (b >= 247 && b <= 250) st.push((b - 247) * 256 + d[i++]! + 108);
        else if (b >= 251 && b <= 254) st.push(-(b - 251) * 256 - d[i++]! - 108);
    }
    return out;
}

interface Priv { subrs: Index | null; defaultW: number; nominalW: number }

export class CffFont implements FontProgram {
    unitsPerEm = 1000;
    numGlyphs = 0;
    private charStrings!: Index;
    private gsubrs: Index = { count: 0, off: [], data: new Uint8Array(0) };
    private privs: Priv[] = [];
    private fdSelect: Uint8Array | null = null;
    private strings: Index = { count: 0, off: [], data: new Uint8Array(0) };
    private charset: number[] = [];
    private gidBySid = new Map<number, number>();
    private nameToGid = new Map<string, number>();
    isCID = false;
    /** The code → glyph id of the font's built-in encoding. */
    builtin = new Map<number, number>();
    private fdMatrices: Array<number[] | null> = [];
    private cache = new Map<number, { path: GlyphPath; width: number } | null>();
    fontMatrix: number[] = [0.001, 0, 0, 0.001, 0, 0];

    constructor(private readonly d: Uint8Array) {
        const hdr = d[2]!;
        const names = readIndex(d, hdr);
        const tops = readIndex(d, names.end);
        const strs = readIndex(d, tops.end);
        this.strings = strs.idx;
        this.gsubrs = readIndex(d, strs.end).idx;
        const top = readDict(item(tops.idx, 0));
        const cs = top.get(17)?.[0];
        if (cs === undefined) throw new Error("no charstrings");
        this.charStrings = readIndex(d, cs).idx;
        this.numGlyphs = this.charStrings.count;
        const fm = top.get(1207);
        if (fm && fm.length === 6) { this.fontMatrix = fm; this.unitsPerEm = Math.round(1 / (fm[0]! || 0.001)); }
        this.isCID = top.has(1230);
        const readPrivate = (dict: Map<number, number[]>): Priv => {
            const p = dict.get(18);
            if (!p || p.length < 2) return { subrs: null, defaultW: 0, nominalW: 0 };
            const pd = readDict(d.subarray(p[1]!, p[1]! + p[0]!));
            const so = pd.get(19)?.[0];
            return { subrs: so !== undefined ? readIndex(d, p[1]! + so).idx : null, defaultW: pd.get(20)?.[0] ?? 0, nominalW: pd.get(21)?.[0] ?? 0 };
        };
        if (this.isCID) {
            const fda = top.get(1236)?.[0];
            if (fda !== undefined) {
                const fdi = readIndex(d, fda).idx;
                for (let i = 0; i < fdi.count; i++) { const fd = readDict(item(fdi, i)); this.privs.push(readPrivate(fd)); this.fdMatrices.push(fd.get(1207) ?? null); }
            }
            const fs = top.get(1237)?.[0];
            if (fs !== undefined) this.fdSelect = this.readFdSelect(fs);
        } else this.privs.push(readPrivate(top));
        this.readCharset(top.get(15)?.[0] ?? 0);
        if (!this.isCID) this.readEncoding(top.get(16)?.[0] ?? 0);
    }

    private readFdSelect(pos: number): Uint8Array {
        const d = this.d, n = this.numGlyphs;
        const out = new Uint8Array(n);
        const fmt = d[pos]!;
        if (fmt === 0) for (let i = 0; i < n; i++) out[i] = d[pos + 1 + i]!;
        else if (fmt === 3) {
            const nr = (d[pos + 1]! << 8) | d[pos + 2]!;
            let p = pos + 3;
            for (let r = 0; r < nr; r++) {
                const first = (d[p]! << 8) | d[p + 1]!, fd = d[p + 2]!, next = (d[p + 3]! << 8) | d[p + 4]!;
                for (let g = first; g < next && g < n; g++) out[g] = fd;
                p += 3;
            }
        }
        return out;
    }

    private sidString(sid: number): string {
        if (sid < STD.length) return STD[sid]!;
        if (sid < 391) return `sid${sid}`;
        const i = sid - 391;
        if (i >= this.strings.count) return `sid${sid}`;
        let s = "";
        const b = item(this.strings, i);
        for (let k = 0; k < b.length; k++) s += String.fromCharCode(b[k]!);
        return s;
    }

    private readCharset(off: number) {
        const d = this.d, n = this.numGlyphs;
        const cs: number[] = [0];
        if (off <= 2 && !this.isCID) {
            // predefined charsets: ISOAdobe, Expert, ExpertSubset → the first n SIDs of ISOAdobe
            for (let i = 1; i < n; i++) cs.push(i);
        } else {
            const fmt = d[off]!;
            let p = off + 1;
            if (fmt === 0) for (let i = 1; i < n; i++) { cs.push((d[p]! << 8) | d[p + 1]!); p += 2; }
            else {
                while (cs.length < n) {
                    const first = (d[p]! << 8) | d[p + 1]!;
                    p += 2;
                    let left: number;
                    if (fmt === 1) left = d[p++]!; else { left = (d[p]! << 8) | d[p + 1]!; p += 2; }
                    for (let k = 0; k <= left && cs.length < n; k++) cs.push(first + k);
                }
            }
        }
        this.charset = cs;
        cs.forEach((sid, gid) => { this.gidBySid.set(sid, gid); if (!this.isCID) { const nm = this.sidString(sid); if (!this.nameToGid.has(nm)) this.nameToGid.set(nm, gid); } });
    }

    private readEncoding(off: number) {
        const d = this.d;
        if (off === 0 || off === 1) {
            // Standard / Expert: code → name via the Standard encoding
            const std: Record<number, string> = {};
            // StandardEncoding names by code are the glyph order of the first 149 standard strings for codes 32..126 and some high codes; approximate with ASCII.
            for (let c = 32; c < 127; c++) std[c] = STD[c - 31]!;
            std[39] = "quoteright"; std[96] = "quoteleft";
            for (const [c, n] of Object.entries(std)) { const g = this.nameToGid.get(n); if (g !== undefined) this.builtin.set(+c, g); }
            return;
        }
        const fmt = d[off]! & 0x7f;
        let p = off + 1;
        if (fmt === 0) { const n = d[p++]!; for (let i = 0; i < n; i++) this.builtin.set(d[p++]!, i + 1); }
        else if (fmt === 1) {
            const nr = d[p++]!;
            let gid = 1;
            for (let r = 0; r < nr; r++) { const first = d[p++]!, left = d[p++]!; for (let k = 0; k <= left; k++) this.builtin.set(first + k, gid++); }
        }
        if (d[off]! & 0x80) {
            const ns = d[p++]!;
            for (let i = 0; i < ns; i++) { const code = d[p]!, sid = (d[p + 1]! << 8) | d[p + 2]!; p += 3; const g = this.gidBySid.get(sid); if (g !== undefined) this.builtin.set(code, g); }
        }
    }

    gidByName(name: string): number { return this.nameToGid.get(name) ?? -1; }
    gidByUnicode(_u: number): number { return 0; }
    /** The glyph id of a CID in a CID-keyed font. */
    gidByCid(cid: number): number { return this.isCID ? this.gidBySid.get(cid) ?? 0 : cid; }
    glyphName(gid: number): string { return this.isCID ? "" : this.sidString(this.charset[gid] ?? 0); }

    advance(gid: number): number { return this.run(gid)?.width ?? -1; }
    glyph(gid: number): GlyphPath | null { return this.run(gid)?.path ?? null; }

    private run(gid: number): { path: GlyphPath; width: number } | null {
        if (this.cache.has(gid)) return this.cache.get(gid)!;
        let r: { path: GlyphPath; width: number } | null = null;
        if (gid >= 0 && gid < this.numGlyphs) { try { r = this.interpret(gid, 0); } catch { r = null; } }
        if (this.cache.size > 4000) this.cache.clear();
        this.cache.set(gid, r);
        return r;
    }

    private interpret(gid: number, depth: number): { path: GlyphPath; width: number } {
        const fd = this.fdSelect ? this.fdSelect[gid] ?? 0 : 0;
        const priv = this.privs[fd] ?? this.privs[0] ?? { subrs: null, defaultW: 0, nominalW: 0 };
        const subrs = priv.subrs, gsubrs = this.gsubrs;
        const bias = (n: number) => (n < 1240 ? 107 : n < 33900 ? 1131 : 32768);
        const path: GlyphPath = [];
        const stack: number[] = [];
        let x = 0, y = 0, nStems = 0, width = priv.defaultW, haveWidth = false, open = false;
        const trans: number[] = new Array(32).fill(0);
        const fmx = this.fdMatrices[fd];
        void fmx;
        const move = (dx: number, dy: number) => { if (open) path.push(4); x += dx; y += dy; path.push(0, x, y); open = true; };
        const line = (dx: number, dy: number) => { x += dx; y += dy; path.push(1, x, y); };
        const curve = (a: number, b: number, c: number, d: number, e: number, f: number) => { const x1 = x + a, y1 = y + b, x2 = x1 + c, y2 = y1 + d; x = x2 + e; y = y2 + f; path.push(3, x1, y1, x2, y2, x, y); };
        const takeWidth = (expect: number, even = false) => {
            if (haveWidth) return;
            haveWidth = true;
            const extra = even ? stack.length % 2 === 1 : stack.length > expect;
            if (extra) width = priv.nominalW + stack.shift()!;
        };
        let done = false;
        const exec = (code: Uint8Array, d: number) => {
            if (d > 12) return;
            let i = 0;
            while (i < code.length && !done) {
                const b = code[i++]!;
                if (b >= 32 && b <= 246) stack.push(b - 139);
                else if (b >= 247 && b <= 250) stack.push((b - 247) * 256 + code[i++]! + 108);
                else if (b >= 251 && b <= 254) stack.push(-(b - 251) * 256 - code[i++]! - 108);
                else if (b === 255) { stack.push(((code[i]! << 24) | (code[i + 1]! << 16) | (code[i + 2]! << 8) | code[i + 3]!) / 65536); i += 4; }
                else if (b === 28) { stack.push(((code[i]! << 8) | code[i + 1]!) << 16 >> 16); i += 2; }
                else switch (b) {
                    case 1: case 3: case 18: case 23: takeWidth(0, true); nStems += stack.length >> 1; stack.length = 0; break;
                    case 19: case 20: takeWidth(0, true); nStems += stack.length >> 1; stack.length = 0; i += (nStems + 7) >> 3; break;
                    case 21: takeWidth(2); if (stack.length >= 2) move(stack[stack.length - 2]!, stack[stack.length - 1]!); stack.length = 0; break;
                    case 22: takeWidth(1); if (stack.length >= 1) move(stack[stack.length - 1]!, 0); stack.length = 0; break;
                    case 4: takeWidth(1); if (stack.length >= 1) move(0, stack[stack.length - 1]!); stack.length = 0; break;
                    case 5: for (let k = 0; k + 1 < stack.length; k += 2) line(stack[k]!, stack[k + 1]!); stack.length = 0; break;
                    case 6: case 7: { let h = b === 6; for (const v of stack) { if (h) line(v, 0); else line(0, v); h = !h; } stack.length = 0; break; }
                    case 8: for (let k = 0; k + 5 < stack.length; k += 6) curve(stack[k]!, stack[k + 1]!, stack[k + 2]!, stack[k + 3]!, stack[k + 4]!, stack[k + 5]!); stack.length = 0; break;
                    case 24: { let k = 0; for (; k + 7 < stack.length; k += 6) curve(stack[k]!, stack[k + 1]!, stack[k + 2]!, stack[k + 3]!, stack[k + 4]!, stack[k + 5]!); line(stack[k]!, stack[k + 1]!); stack.length = 0; break; }
                    case 25: { let k = 0; for (; k + 7 < stack.length; k += 2) line(stack[k]!, stack[k + 1]!); curve(stack[k]!, stack[k + 1]!, stack[k + 2]!, stack[k + 3]!, stack[k + 4]!, stack[k + 5]!); stack.length = 0; break; }
                    case 26: { let k = 0, dx1 = 0; if (stack.length % 4 === 1) dx1 = stack[k++]!; for (; k + 3 < stack.length; k += 4) { curve(dx1, stack[k]!, stack[k + 1]!, stack[k + 2]!, 0, stack[k + 3]!); dx1 = 0; } stack.length = 0; break; }
                    case 27: { let k = 0, dy1 = 0; if (stack.length % 4 === 1) dy1 = stack[k++]!; for (; k + 3 < stack.length; k += 4) { curve(stack[k]!, dy1, stack[k + 1]!, stack[k + 2]!, stack[k + 3]!, 0); dy1 = 0; } stack.length = 0; break; }
                    case 30: case 31: {
                        let h = b === 31, k = 0;
                        while (k + 3 < stack.length) {
                            const last = stack.length - k === 5;
                            if (h) curve(stack[k]!, 0, stack[k + 1]!, stack[k + 2]!, last ? stack[k + 4]! : 0, stack[k + 3]!);
                            else curve(0, stack[k]!, stack[k + 1]!, stack[k + 2]!, stack[k + 3]!, last ? stack[k + 4]! : 0);
                            h = !h;
                            k += 4;
                        }
                        stack.length = 0;
                        break;
                    }
                    case 10: { const n = stack.pop()! + bias(subrs?.count ?? 0); if (subrs && n >= 0 && n < subrs.count) exec(item(subrs, n), d + 1); break; }
                    case 29: { const n = stack.pop()! + bias(gsubrs.count); if (n >= 0 && n < gsubrs.count) exec(item(gsubrs, n), d + 1); break; }
                    case 11: return;
                    case 14: {
                        takeWidth(0, stack.length === 1 || stack.length === 5);
                        if (stack.length >= 4 && depth < 2) {
                            // seac: adx ady bchar achar (standard encoding codes)
                            const [adx, ady, bch, ach] = stack.slice(-4) as [number, number, number, number];
                            const sub = (code: number, ox: number, oy: number) => {
                                const nm = STD[code === 39 ? 8 : code === 96 ? 65 : code - 31];
                                const g = nm ? this.nameToGid.get(nm) : undefined;
                                if (g === undefined) return;
                                const r = this.interpret(g, depth + 1).path;
                                let k = 0;
                                while (k < r.length) { const op = r[k++]!; path.push(op); const n = op === 0 || op === 1 ? 1 : op === 2 ? 2 : op === 3 ? 3 : 0; for (let q = 0; q < n; q++) { path.push(r[k++]! + ox, r[k++]! + oy); } }
                            };
                            if (open) { path.push(4); open = false; }
                            sub(bch, 0, 0);
                            sub(ach, adx, ady);
                        }
                        if (open) { path.push(4); open = false; }
                        done = true;
                        return;
                    }
                    case 12: {
                        const e = code[i++]!;
                        switch (e) {
                            case 34: { const [a, b2, c, dd, ee, f, g] = stack; const y0 = y; curve(a!, 0, b2!, c!, dd!, 0); curve(ee!, 0, f!, -c! , g!, 0); void y0; stack.length = 0; break; }
                            case 35: { for (let k = 0; k < 2; k++) curve(stack[k * 6]!, stack[k * 6 + 1]!, stack[k * 6 + 2]!, stack[k * 6 + 3]!, stack[k * 6 + 4]!, stack[k * 6 + 5]!); stack.length = 0; break; }
                            case 36: { const [a, b2, c, dd, ee, f, g, h2, i2] = stack; curve(a!, b2!, c!, dd!, ee!, 0); curve(f!, 0, g!, h2!, i2!, -(b2! + dd! + h2!)); stack.length = 0; break; }
                            case 37: {
                                const s = stack.slice();
                                const dx = s[0]! + s[2]! + s[4]! + s[6]! + s[8]!, dy = s[1]! + s[3]! + s[5]! + s[7]! + s[9]!;
                                curve(s[0]!, s[1]!, s[2]!, s[3]!, s[4]!, s[5]!);
                                if (Math.abs(dx) > Math.abs(dy)) curve(s[6]!, s[7]!, s[8]!, s[9]!, s[10]!, -dy); else curve(s[6]!, s[7]!, s[8]!, s[9]!, -dx, s[10]!);
                                stack.length = 0;
                                break;
                            }
                            case 3: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(a && b2 ? 1 : 0); break; }
                            case 4: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(a || b2 ? 1 : 0); break; }
                            case 5: stack.push(stack.pop() ? 0 : 1); break;
                            case 9: stack.push(Math.abs(stack.pop()!)); break;
                            case 10: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(a + b2); break; }
                            case 11: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(a - b2); break; }
                            case 12: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(b2 ? a / b2 : 0); break; }
                            case 14: stack.push(-stack.pop()!); break;
                            case 15: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(a === b2 ? 1 : 0); break; }
                            case 18: stack.pop(); break;
                            case 20: { const k = stack.pop()!, v = stack.pop()!; trans[k] = v; break; }
                            case 21: stack.push(trans[stack.pop()!] ?? 0); break;
                            case 22: { const v2 = stack.pop()!, v1 = stack.pop()!, s2 = stack.pop()!, s1 = stack.pop()!; stack.push(v1 <= v2 ? s1 : s2); break; }
                            case 23: stack.push(0.5); break;
                            case 24: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(a * b2); break; }
                            case 26: stack.push(Math.sqrt(stack.pop()!)); break;
                            case 27: { const a = stack.pop()!; stack.push(a, a); break; }
                            case 28: { const b2 = stack.pop()!, a = stack.pop()!; stack.push(b2, a); break; }
                            case 29: { const i2 = stack.pop()!; stack.push(stack[stack.length - 1 - Math.max(0, i2)] ?? 0); break; }
                            case 30: { const j = stack.pop()!, n = stack.pop()!; if (n > 0 && n <= stack.length) { const part = stack.splice(stack.length - n, n); const s = ((j % n) + n) % n; for (let k = 0; k < n; k++) stack.push(part[(k - s + n) % n]!); } break; }
                            default: stack.length = 0;
                        }
                        break;
                    }
                    default: stack.length = 0;
                }
            }
        };
        exec(item(this.charStrings, gid), 0);
        if (open) path.push(4);
        return { path, width };
    }
}
